import { useEffect, useState } from "react";
import api, { API, formatApiError, getApiEmpresa } from "@/lib/api";
import { fmtFechaHora } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Desktop, Copy, Warning, Trash, Key, WifiSlash, Fingerprint } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Checadores de escritorio CHKR (carpeta desktop/ del repo): alta, listado y baja.
 *
 * Al dar de alta uno, el backend entrega su token en claro UNA sola vez (solo guarda
 * su sha256). Por eso el diálogo pasa a una segunda vista con el bloque listo para
 * pegar en el config.json del checador, y avisa que no se podrá volver a ver.
 *
 * "Huellas en la nube": quién quedó enrolado (en cualquier checador) y por eso puede
 * marcar en todos los de su empresa. Borrar un enrolamiento lo quita de todos los
 * checadores en su siguiente sincronización.
 */
const ETIQUETA_MARCA = {
  entrada: "Entrada",
  inicio_descanso: "Salida a descanso",
  fin_descanso: "Regreso de descanso",
  salida: "Salida",
};

// El checador se reporta cada minuto; tras esto sin noticias se marca sin conexión.
// (El aviso por WhatsApp a los encargados sale más tarde, a las 2 h: ver
// backend/conexion_dispositivos.py.)
const SIN_CONEXION_MS = 15 * 60 * 1000;

function sinConexion(d) {
  return d.activo !== false && d.ultima_conexion
    && Date.now() - new Date(d.ultima_conexion).getTime() > SIN_CONEXION_MS;
}

// Valor del Select para "sin ubicación": Radix no admite "" como valor de un item.
const SIN_UBICACION = "__sin_ubicacion__";

function bloqueConfig(disp) {
  const bloque = {
    empresa_id: disp.empresa_id,
    centro_id: disp.centro_id,
    zona_horaria: disp.zona_horaria || "America/Mexico_City",
    nube: { base_url: API, device_id: disp.id, token: disp.token },
  };
  // Sin las llaves exteriores y con coma final: se pega justo debajo de la "{" de
  // config.json, en lugar de las líneas con esos mismos nombres.
  return JSON.stringify(bloque, null, 2).slice(2, -2) + ",";
}

export default function ChecadoresCHKR() {
  const [items, setItems] = useState([]);
  const [centros, setCentros] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ empresa_id: "", centro_id: SIN_UBICACION, nombre: "" });
  const [creado, setCreado] = useState(null);  // respuesta del alta, con token
  const [busy, setBusy] = useState(false);
  const [problemas, setProblemas] = useState(null);  // {dispositivo, eventos}
  const [pinDe, setPinDe] = useState(null);  // checador al que se le define PIN
  const [pin, setPin] = useState({ nuevo: "", confirmar: "" });
  const [huellas, setHuellas] = useState([]);
  const [verHuellas, setVerHuellas] = useState(false);

  const abrirPin = (d) => { setPin({ nuevo: "", confirmar: "" }); setPinDe(d); };

  const guardarPin = async () => {
    if (!/^\d{4,8}$/.test(pin.nuevo)) { toast.error("El PIN debe tener de 4 a 8 dígitos"); return; }
    if (pin.nuevo !== pin.confirmar) { toast.error("Los PIN no coinciden"); return; }
    setBusy(true);
    try {
      await api.put(`/dispositivos/${pinDe.id}/pin`, { pin: pin.nuevo });
      toast.success("PIN guardado. El checador lo recibirá en menos de un minuto si tiene internet.");
      setPinDe(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verProblemas = async (d) => {
    try {
      const { data } = await api.get(`/dispositivos/${d.id}/marcas-con-problema`);
      setProblemas({ dispositivo: d, eventos: data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = async () => {
    try {
      const [d, c, e, h] = await Promise.all([
        api.get("/dispositivos"), api.get("/centros"), api.get("/empresas"), api.get("/dispositivos/plantillas"),
      ]);
      setItems(d.data);
      setCentros(c.data.filter((x) => x.activo !== false));
      setEmpresas(e.data);
      setHuellas(h.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const centro = (id) => centros.find((c) => c.id === id);
  const empresaNombre = (id) => empresas.find((e) => e.id === id)?.razon_social;
  const centrosDeEmpresa = centros.filter((c) => c.empresa_id === form.empresa_id);
  const ordenados = [...items].sort((a, b) => (b.activo !== false) - (a.activo !== false));

  const startCreate = () => {
    setCreado(null);
    // Empresa activa del super_admin, o la única que ve un admin de empresa.
    const empresaInicial = empresas.find((e) => e.id === getApiEmpresa())?.id || (empresas.length === 1 ? empresas[0].id : "");
    setForm({ empresa_id: empresaInicial, centro_id: SIN_UBICACION, nombre: "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.empresa_id || !form.nombre.trim()) {
      toast.error("Elige la empresa y escribe un nombre para el checador");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/dispositivos", {
        ...form, centro_id: form.centro_id === SIN_UBICACION ? null : form.centro_id,
      });
      setCreado(data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado. Pégalo en el config.json del checador.");
    } catch {
      toast.error("No se pudo copiar; selecciona el texto y cópialo a mano.");
    }
  };

  const darDeBaja = async (d) => {
    if (!window.confirm(`¿Dar de baja "${d.nombre}"? Dejará de poder enviar marcas a la nube.`)) return;
    try {
      await api.delete(`/dispositivos/${d.id}`);
      toast.success("Checador dado de baja");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const borrarHuella = async (h) => {
    if (!window.confirm(
      `¿Borrar la huella de ${h.empleado_nombre || "este empleado"}? Se quitará de todos los checadores `
      + "en menos de un minuto y tendrá que volver a enrolarse para checar con huella.",
    )) return;
    try {
      await api.delete(`/dispositivos/plantillas/${h.empleado_id}`);
      toast.success("Huella borrada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cerrar = (v) => {
    if (!v && creado && !window.confirm("¿Ya copiaste el bloque? El token no se podrá volver a ver.")) return;
    setOpen(v);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <p className="text-sm text-slate-600 max-w-2xl">
          Da de alta cada checador de escritorio CHKR para que pueda enviar sus marcas a la nube. Cualquier
          empleado de la empresa puede marcar en cualquiera de sus checadores. Al crearlo obtendrás el bloque
          de credenciales que va en su <span className="font-mono">config.json</span>.
        </p>
        <Button onClick={startCreate} className="bg-brand hover:bg-brand-hover" data-testid="checador-new-btn" data-tour="checadores-nuevo">
          <Plus size={16} weight="bold" className="mr-2" /> Nuevo checador
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="checadores-tabla">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Checador</th>
                <th>Empresa / ubicación</th>
                <th>Última conexión</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ordenados.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-500 py-8">Sin checadores registrados</td></tr>
              )}
              {ordenados.map((d) => {
                const activo = d.activo !== false;
                const c = centro(d.centro_id);
                return (
                  <tr key={d.id} data-testid={`checador-row-${d.id}`} className={activo ? "" : "opacity-50"}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-100 rounded flex items-center justify-center text-slate-700">
                          <Desktop size={18} weight="duotone" />
                        </div>
                        <div>
                          <div className="font-semibold text-ink">{d.nombre}</div>
                          <div className="text-xs text-slate-500 font-mono">{d.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm">
                      <div>{empresaNombre(d.empresa_id) || "—"}</div>
                      <div className="text-xs text-slate-500">{c?.nombre || "Sin ubicación fija"}</div>
                    </td>
                    <td className="font-mono text-xs">
                      {d.ultima_conexion ? fmtFechaHora(d.ultima_conexion, d.zona_horaria || c?.zona_horaria) : "Nunca"}
                      {d.ultima_sincronizacion && (
                        <div className="text-slate-400 font-sans mt-0.5">
                          Última marca recibida: {fmtFechaHora(d.ultima_sincronizacion, d.zona_horaria || c?.zona_horaria)}
                        </div>
                      )}
                    </td>
                    <td>
                      {!activo ? (
                        <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">Dado de baja</span>
                      ) : d.cadena_rota ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-50 text-red-700"
                          title={d.cadena_rota.motivo}
                        >
                          <Warning size={12} weight="bold" /> Marcas rechazadas
                        </span>
                      ) : sinConexion(d) ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-50 text-amber-800">
                          <WifiSlash size={12} weight="bold" /> Sin conexión
                        </span>
                      ) : !d.ultima_conexion ? (
                        <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">Sin instalar</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700">Activo</span>
                      )}
                      {activo && !d.tiene_pin && (
                        <button
                          type="button"
                          onClick={() => abrirPin(d)}
                          className="block mt-1 text-xs text-amber-700 underline hover:text-amber-900"
                        >
                          Definir PIN en el panel
                        </button>
                      )}
                      {activo && d.cadena_rota && (
                        <div className="text-xs text-red-600 mt-1 max-w-xs">{d.cadena_rota.motivo}</div>
                      )}
                      {d.marcas_con_problema > 0 && (
                        <button
                          type="button"
                          onClick={() => verProblemas(d)}
                          className="block mt-1 text-xs text-amber-700 underline hover:text-amber-900"
                          data-testid={`checador-problemas-${d.id}`}
                        >
                          {d.marcas_con_problema} {d.marcas_con_problema === 1 ? "marca no aplicada" : "marcas no aplicadas"}
                        </button>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      {activo && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => abrirPin(d)} title="PIN del encargado" data-testid={`checador-pin-${d.id}`}>
                            <Key size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => darDeBaja(d)} title="Dar de baja" data-testid={`checador-baja-${d.id}`}>
                            <Trash size={16} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-md px-4 py-3">
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <Fingerprint size={20} weight="duotone" className="text-slate-700 shrink-0" />
          <span>
            <strong className="text-ink">{huellas.length}</strong>{" "}
            {huellas.length === 1 ? "empleado enrolado" : "empleados enrolados"} en la nube. Basta con enrolar a
            alguien en un checador para que pueda marcar en cualquiera de su empresa.
          </span>
        </div>
        {huellas.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setVerHuellas(true)} data-testid="checador-huellas-btn">
            Ver huellas
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={cerrar}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="checador-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{creado ? "Credenciales del checador" : "Nuevo checador"}</DialogTitle>
          </DialogHeader>

          {!creado ? (
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1">
                <Label className="label-uppercase">Empresa</Label>
                <Select
                  value={form.empresa_id}
                  onValueChange={(v) => setForm({ ...form, empresa_id: v, centro_id: SIN_UBICACION })}
                >
                  <SelectTrigger data-testid="checador-empresa-select"><SelectValue placeholder="Selecciona la empresa" /></SelectTrigger>
                  <SelectContent>
                    {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Todos los empleados de esta empresa podrán marcar en este checador.</p>
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Ubicación (opcional)</Label>
                <Select
                  value={form.centro_id}
                  onValueChange={(v) => setForm({ ...form, centro_id: v })}
                  disabled={!form.empresa_id}
                >
                  <SelectTrigger data-testid="checador-centro-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SIN_UBICACION}>Sin ubicación fija</SelectItem>
                    {centrosDeEmpresa.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Centro donde está el checador: define su zona horaria y el centro que se muestra primero al enrolar.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="label-uppercase">Nombre</Label>
                <Input
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="p. ej. Recepción planta baja"
                  data-testid="checador-nombre-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                <Warning size={18} weight="bold" className="mt-0.5 shrink-0" />
                <span>
                  Copia este bloque ahora: <strong>el token no se podrá volver a ver</strong>. Si se pierde,
                  da de baja este checador y crea uno nuevo.
                </span>
              </div>
              <pre className="font-mono text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto select-all" data-testid="checador-config">
                {bloqueConfig(creado)}
              </pre>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
                <li>En el equipo del checador, abre <span className="font-mono">config.json</span> (junto a <span className="font-mono">Checador.exe</span>) con el Bloc de notas.</li>
                <li>Borra las líneas que ya tenga con esos mismos nombres (<span className="font-mono">empresa_id</span>, <span className="font-mono">centro_id</span>, <span className="font-mono">zona_horaria</span>, <span className="font-mono">nube</span>) y pega el bloque justo debajo de la primera llave <span className="font-mono">{"{"}</span>.</li>
                <li>Guarda y vuelve a abrir el checador. Las marcas se envían en cuanto alguien checa y cada minuto.</li>
              </ol>
            </div>
          )}

          <DialogFooter>
            {!creado ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="checador-save-btn">
                  {busy ? "Creando…" : "Crear checador"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => cerrar(false)}>Listo</Button>
                <Button
                  onClick={() => copiar(bloqueConfig(creado))}
                  className="bg-brand hover:bg-brand-hover"
                  data-testid="checador-copiar-btn"
                >
                  <Copy size={16} weight="bold" className="mr-2" /> Copiar bloque
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinDe} onOpenChange={(v) => !v && setPinDe(null)}>
        <DialogContent className="max-w-md" data-testid="checador-pin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">PIN del encargado — {pinDe?.nombre}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Con este PIN se entra a "Administrar" en el checador para enrolar huellas.
            {pinDe?.tiene_pin ? " Reemplaza al PIN actual." : ""} Lo recibe en menos de un minuto si tiene internet.
          </p>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1">
              <Label className="label-uppercase">PIN nuevo (4 a 8 dígitos)</Label>
              <Input
                type="password" inputMode="numeric" autoComplete="new-password" maxLength={8}
                value={pin.nuevo}
                onChange={(e) => setPin({ ...pin, nuevo: e.target.value.replace(/\D/g, "") })}
                data-testid="checador-pin-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="label-uppercase">Confirmar PIN</Label>
              <Input
                type="password" inputMode="numeric" autoComplete="new-password" maxLength={8}
                value={pin.confirmar}
                onChange={(e) => setPin({ ...pin, confirmar: e.target.value.replace(/\D/g, "") })}
                data-testid="checador-pin-confirmar"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDe(null)}>Cancelar</Button>
            <Button onClick={guardarPin} disabled={busy} className="bg-brand hover:bg-brand-hover" data-testid="checador-pin-guardar">
              {busy ? "Guardando…" : "Guardar PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!problemas} onOpenChange={(v) => !v && setProblemas(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="checador-problemas-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Marcas no aplicadas — {problemas?.dispositivo.nombre}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Llegaron a la nube y quedan guardadas, pero no se pudieron llevar a una jornada. Si hace falta,
            corrige la jornada desde Jornadas con una captura manual.
          </p>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Empleado</th><th>Marca</th><th>Hora</th><th>Motivo</th></tr>
              </thead>
              <tbody>
                {problemas?.eventos.map((ev) => (
                  <tr key={ev.id}>
                    <td className="text-sm">{ev.empleado_nombre || <span className="text-slate-400">Desconocido</span>}</td>
                    <td className="text-xs uppercase tracking-wider font-bold">{ETIQUETA_MARCA[ev.tipo] || ev.tipo}</td>
                    <td className="font-mono text-xs">
                      {fmtFechaHora(ev.hora, problemas.dispositivo.zona_horaria || centro(problemas.dispositivo.centro_id)?.zona_horaria)}
                    </td>
                    <td className="text-xs text-slate-600">{ev.jornada?.motivo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={verHuellas} onOpenChange={setVerHuellas}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="checador-huellas-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Huellas en la nube</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Cada empleado de la lista puede marcar en cualquier checador de su empresa. Si se le vuelve a enrolar,
            la huella nueva reemplaza a esta en todos los checadores.
          </p>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Empleado</th><th>Centro</th><th>Enrolado en</th><th>Fecha</th><th></th></tr>
              </thead>
              <tbody>
                {huellas.map((h) => (
                  <tr key={`${h.empleado_id}-${h.formato}`}>
                    <td className="text-sm">
                      <div>{h.empleado_nombre || <span className="text-slate-400">Empleado eliminado</span>}</div>
                      {h.numero_empleado && <div className="text-xs text-slate-500 font-mono">{h.numero_empleado}</div>}
                    </td>
                    <td className="text-sm">{centro(h.centro_id)?.nombre || "—"}</td>
                    <td className="text-sm">{h.checador_nombre || "—"}</td>
                    <td className="font-mono text-xs">{fmtFechaHora(h.enrolado_en, centro(h.centro_id)?.zona_horaria)}</td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => borrarHuella(h)} title="Borrar huella"
                        data-testid={`checador-huella-borrar-${h.empleado_id}`}>
                        <Trash size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
